import { React } from '@/lib/react';
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useUser } from './UserContext';
import { Property, PropertySubmitData } from '@/types/property';

interface PropertyContextType {
  properties: Property[];
  loading: boolean;
  error: string | null;
  refreshProperties: () => Promise<void>;
  deleteProperty: (propertyId: string) => Promise<void>;
  updateProperty: (propertyId: string, propertyData: Partial<PropertySubmitData>) => Promise<void>;
  getPropertyById: (propertyId: string) => Property | undefined;
}

const PropertyContext = createContext<PropertyContextType>({
  properties: [],
  loading: true,
  error: null,
  refreshProperties: async () => {},
  deleteProperty: async () => {},
  updateProperty: async () => {},
  getPropertyById: () => undefined,
});

export const PropertyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { userData } = useUser();

  const fetchProperties = async () => {
    if (!userData) {
      setProperties([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('properties')
        .select(`
          *,
          media:property_media!property_media_property_id_fkey(*),
          open_houses:open_houses(*),
          posts:property_posts(*)
        `)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }


      setProperties((data || []) as unknown as Property[]);
    } catch (err) {
      setError('Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };

  const deleteProperty = async (propertyId: string) => {
    if (!userData) return;
    
    try {
      // Delete the property from the database
      const { error: deleteError } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyId)
      
      if (deleteError) {
        throw deleteError;
      }
      
      // Update the local state
      setProperties(prev => prev.filter(property => property.id !== propertyId));
    } catch (err) {
      console.error('Failed to delete property:', err);
      throw err;
    }
  };

  const updateProperty = async (propertyId: string, propertyData: Partial<PropertySubmitData>) => {
    if (!userData) return;
    
    try {
      // Update the property in the database
      const { error: updateError } = await supabase
        .from('properties')
        .update({ 
          ...propertyData,
          updated_at: new Date().toISOString() 
        })
        .eq('id', propertyId)
      
      if (updateError) {
        throw updateError;
      }
      
      // Update the local state - fixed to maintain property type compatibility
      setProperties(prev => 
        prev.map(property => 
          property.id === propertyId 
            ? {
                ...property,
                ...propertyData as Partial<Property>,
                updated_at: new Date().toISOString()
              } as Property
            : property
        )
      );
    } catch (err) {
      console.error('Failed to update property:', err);
      throw err;
    }
  };

  const getPropertyById = (propertyId: string) => {
    return properties.find(property => property.id === propertyId);
  };

  useEffect(() => {
    fetchProperties();
  }, [userData?.id]);

  return (
    <PropertyContext.Provider value={{ 
      properties, 
      loading, 
      error, 
      refreshProperties: fetchProperties,
      deleteProperty,
      updateProperty,
      getPropertyById
    }}>
      {children}
    </PropertyContext.Provider>
  );
};

export const useProperties = () => useContext(PropertyContext); 